import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@mui/material";
import ContentPage from "components/contentPage/ContentPage";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import { BranchFavouriteContext } from "contexts/BranchFavouriteContext";
import { useLocalBranch } from "hooks/LocalStorage";
import { MealType, MealTypeList } from "types/MealType";
import { ICreateCateringEventRequest, EVENT_TYPES, CateringEventTier, CateringEventTierList, TIER_DISPLAY_NAMES } from "types/CateringEvent";
import { createCateringEvent } from "api/CateringApi";
import { invalidateCateringEventsCache } from "hooks/CateringEvents";
import Toast, { IToastBasicProps } from "components/Toast/Toast";
import { format } from "date-fns";
import styles from "./CreateCateringEvent.module.css";

const TIME_SLOTS: { value: string; label: string }[] = [];
for (let h = 0; h < 24; h++) {
  for (const m of [0, 30]) {
    const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? "AM" : "PM";
    const label = `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
    TIME_SLOTS.push({ value, label });
  }
}

interface MealRow {
  eventDate: string;
  mealType: MealType;
  eventTime: string;
  numberOfPeople: number | "";
  eventType: string;
  tier: CateringEventTier | "";
}

const CreateCateringEvent: React.FC = () => {
  const history = useHistory();
  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);
  const { branches } = useContext(BranchFavouriteContext);
  const [selectedBranch] = useLocalBranch(
    branches.length > 0 ? branches[0] : undefined
  );
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = format(tomorrow, "yyyy-MM-dd");

  // Form state
  const [name, setName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [remark, setRemark] = useState("");
  const [meals, setMeals] = useState<MealRow[]>([
    { eventDate: defaultDate, mealType: MealType.LUNCH, eventTime: "", numberOfPeople: "", eventType: "", tier: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showToast, setShowToast] = useState<IToastBasicProps>({
    open: false,
    message: "",
    type: "error",
  });

  if (!selectedBranch) {
    return <></>;
  }

  const addMealRow = () => {
    setMeals((prev) => {
      const last = prev.length > 0 ? prev[prev.length - 1] : null;
      return [...prev, { eventDate: last?.eventDate ?? defaultDate, mealType: MealType.LUNCH, eventTime: "", numberOfPeople: "", eventType: "", tier: "" }];
    });
  };

  const removeMealRow = (idx: number) => {
    setMeals((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateMealRow = (idx: number, update: Partial<MealRow>) => {
    setMeals((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...update };
      return next;
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Event name is required";
    if (!customerName.trim()) errs.customerName = "Customer name is required";
    if (!customerContact.trim()) errs.customerContact = "Customer contact is required";
    if (meals.length === 0) errs.meals = "Add at least one meal";
    for (let i = 0; i < meals.length; i++) {
      if (!meals[i].eventDate) errs[`meal_date_${i}`] = "Date is required";
      if (!meals[i].numberOfPeople || (meals[i].numberOfPeople as number) < 1)
        errs[`meal_people_${i}`] = "Required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    let createdCount = 0;
    try {
      for (const meal of meals) {
        const request: ICreateCateringEventRequest = {
          name: name.trim(),
          branchId: selectedBranch.id,
          eventDate: new Date(meal.eventDate).getTime(),
          mealType: meal.mealType as MealType,
          numberOfPeople: meal.numberOfPeople as number,
          customerName: customerName.trim(),
          customerContactNumber: customerContact || undefined,
          customerEmail: customerEmail || undefined,
          remark: remark || undefined,
          eventTimePerMeal: meal.eventTime
            ? { [meal.mealType]: meal.eventTime }
            : undefined,
          eventType: meal.eventType || undefined,
          tier: meal.tier || undefined,
        };
        const result = await createCateringEvent(request);
        if (result) createdCount++;
      }

      if (createdCount > 0) {
        invalidateCateringEventsCache();
        setShowToast({
          open: true,
          message: `Created ${createdCount} draft event(s). Add menu items to each event.`,
          type: "success",
        });
        setTimeout(() => history.push("/catering"), 1500);
      } else {
        setShowToast({ open: true, message: "Failed to create events", type: "error" });
      }
    } catch {
      setShowToast({ open: true, message: "Failed to create events", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContentPage
      header={
        <>
          <div className={styles.pageName}>Create Catering Events</div>
          <span className={styles.backLink} onClick={() => history.push("/catering")}>
            &larr; Back to Events
          </span>
        </>
      }
      mainContent={
        <div className={styles.container}>
          <Toast
            open={showToast.open}
            message={showToast.message}
            type={showToast.type}
            onClose={() => setShowToast({ ...showToast, open: false })}
          />

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Event Details</div>
            <div className={styles.sectionSubtitle}>Venue: {selectedBranch.name}</div>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label className={styles.label}>Event Name *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Wedding Reception"
                />
                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Customer Name *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
                {errors.customerName && <span className={styles.errorText}>{errors.customerName}</span>}
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Customer Contact *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={customerContact}
                  onChange={(e) => setCustomerContact(e.target.value)}
                />
                {errors.customerContact && <span className={styles.errorText}>{errors.customerContact}</span>}
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Customer Email</label>
                <input
                  type="email"
                  className={styles.input}
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
              </div>
              <div className={styles.formFieldFull}>
                <label className={styles.label}>Remarks</label>
                <textarea
                  className={styles.textarea}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Meals</div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
              Each row creates a separate draft event. Add menu items after creation.
            </div>
            {meals.map((meal, idx) => (
              <div key={idx} className={styles.menuItemRow}>
                <div className={styles.formField} style={{ flex: 1 }}>
                  <label className={styles.label}>Date *</label>
                  <input
                    type="date"
                    className={styles.input}
                    value={meal.eventDate}
                    onChange={(e) => updateMealRow(idx, { eventDate: e.target.value })}
                  />
                  {errors[`meal_date_${idx}`] && (
                    <span className={styles.errorText}>{errors[`meal_date_${idx}`]}</span>
                  )}
                </div>
                <div className={styles.formField} style={{ flex: 1 }}>
                  <label className={styles.label}>Meal Type *</label>
                  <select
                    className={styles.select}
                    value={meal.mealType}
                    onChange={(e) => updateMealRow(idx, { mealType: e.target.value as MealType })}
                  >
                    {MealTypeList.map((mt) => (
                      <option key={mt} value={mt}>
                        {mt.charAt(0) + mt.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.formField} style={{ flex: 1 }}>
                  <label className={styles.label}>Event Time</label>
                  <select
                    className={styles.select}
                    value={meal.eventTime}
                    onChange={(e) => updateMealRow(idx, { eventTime: e.target.value })}
                  >
                    <option value="">Select time</option>
                    {TIME_SLOTS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formField} style={{ flex: 0.5 }}>
                  <label className={styles.label}>Pax *</label>
                  <input
                    type="number"
                    className={styles.input}
                    min={1}
                    value={meal.numberOfPeople}
                    onChange={(e) =>
                      updateMealRow(idx, { numberOfPeople: e.target.value ? parseInt(e.target.value) : "" })
                    }
                  />
                  {errors[`meal_people_${idx}`] && (
                    <span className={styles.errorText}>{errors[`meal_people_${idx}`]}</span>
                  )}
                </div>
                <div className={styles.formField} style={{ flex: 1 }}>
                  <label className={styles.label}>Event Type</label>
                  <select
                    className={styles.select}
                    value={meal.eventType}
                    onChange={(e) => updateMealRow(idx, { eventType: e.target.value })}
                  >
                    <option value="">Select type</option>
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formField} style={{ flex: 1 }}>
                  <label className={styles.label}>Tier</label>
                  <select
                    className={styles.select}
                    value={meal.tier}
                    onChange={(e) => updateMealRow(idx, { tier: e.target.value as CateringEventTier | "" })}
                  >
                    <option value="">Select tier</option>
                    {CateringEventTierList.map((t) => (
                      <option key={t} value={t}>{TIER_DISPLAY_NAMES[t]}</option>
                    ))}
                  </select>
                </div>
                {meals.length > 1 && (
                  <button
                    className={styles.removeBtn}
                    onClick={() => removeMealRow(idx)}
                    style={{ marginTop: 18 }}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            <button className={styles.addMenuBtn} onClick={addMealRow}>
              + Add Meal
            </button>
            {errors.meals && <span className={styles.errorText}>{errors.meals}</span>}
          </div>

          <div className={styles.submitRow}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              style={{ backgroundColor: primaryColor, color: primaryTextColor }}
            >
              {submitting ? "Creating..." : `Create ${meals.length} Event(s)`}
            </Button>
            <Button variant="outlined" onClick={() => history.push("/catering")} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default CreateCateringEvent;
